import { useMemo, useState, type ComponentType } from 'react';
import {
  alpha, Alert, Box, Button, Card, CardContent, Checkbox, Chip, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  FormGroup, Grid, IconButton, LinearProgress, Link, Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowDownwardRounded, ArrowUpwardRounded, AutoAwesomeRounded, CheckCircleRounded, RadioButtonUncheckedRounded, TuneRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import AssistantPanel from '@/components/assistant/AssistantPanel';
import Can from '@/components/common/Can';
import DataState from '@/components/common/DataState';
import PageHeader from '@/components/common/PageHeader';
import ResponsiveDialog from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/components/common/useNotify';
import type { SetupGuide, SetupStep } from '@/api/setupGuide.api';
import { useSetupGuide, useSkipSetupStep } from '@/hooks/useSetupGuide';
import { useAllowedTiles, useHomeLayout, useTileAllowed } from '@/hooks/useHomeLayout';
import {
  DEFAULT_HOME_CARDS, HOME_CARDS, HOME_LAYOUT_KEY, homeApi, homeCardMeta, MAX_SHORTCUTS, type HomeLayout, type HomePreferences,
} from '@/api/home.api';
import { mastersApi, MASTERS_SUMMARY_KEY, type MasterTileCount } from '@/api/masters.api';
import { ALL_HUB_TILES, findHubTile, type HubTile, type HubTileRef } from '@/components/layout/setupHubs';
import { formatDate } from '@/utils/format';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APPROVALS_KEY, approvalsApi } from '@/api/vendors.api';
import { HOTEL_APPROVALS_KEY, hotelReviewApi } from '@/api/hotels.api';
import { MAPPINGS_KEY, TRANSPORT_APPROVALS_KEY, mappingsApi, reviewApi as transportReviewApi } from '@/api/transport.api';
import { CUSTOMER_APPROVALS_KEY, LEAD_HOME_KEY, OPEN_STAGES, STAGE_LABEL, customersApi, leadsApi } from '@/api/crm.api';
import { LEAD_VIEW } from '@/components/crm/CrmBits';

/**
 * FR-NAV-07: the Home cards. Each one hides itself when the person may not see it or it has nothing to say; the order comes
 * from the server-resolved layout (role default, then the person's own order). Time-sensitive cards (FR-NAV-08) are always
 * in that list and can be moved, never hidden.
 */
const CARD_COMPONENTS: Record<string, ComponentType> = {
  approvals: ApprovalsCard,
  'followups-today': FollowUpsTodayCard,
  'transport-expiry': TransportExpiryCard,
  'guided-setup': GuidedSetupHomeCard,
  'masters-attention': MastersAttentionCard,
  'my-leads': MyLeadsCard,
  shortcuts: ShortcutsCard,
};

/**
 * Home (Module 0, O11): welcome with the trial, the trial banner, then the person's Home cards in their order, with
 * "Why this order" and the AI assistant beside them.
 */
export default function DashboardPage() {
  const { profile, hasAnyPermission } = useAuth();
  const guide = useSetupGuide();
  const layout = useHomeLayout();
  const [customising, setCustomising] = useState(false);
  const g = guide.data;

  const subtitle = g ? [g.companyName, trialSummary(g)].filter(Boolean).join(' · ') : undefined;
  const firstName = profile?.firstName?.toUpperCase();
  // While the layout loads, or when it cannot be read, Home still works in the built-in order.
  const order = layout.data?.cards?.length ? layout.data.cards : DEFAULT_HOME_CARDS;
  const cards = order.filter((key, i) => {
    const meta = homeCardMeta(key);
    return !!CARD_COMPONENTS[key] && order.indexOf(key) === i && (!meta?.perms?.length || hasAnyPermission(meta.perms));
  });

  return (
    <Box>
      <PageHeader
        title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
        subtitle={subtitle}
        breadcrumbs={[{ label: 'Home' }]}
        action={(
          <Button variant="outlined" size="small" startIcon={<TuneRounded />} onClick={() => setCustomising(true)} disabled={!layout.data}>
            Customise Home
          </Button>
        )}
      />

      {g && <TrialBanner guide={g} />}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 8 }}>
          {cards.map((key) => {
            const CardComponent = CARD_COMPONENTS[key];
            return <CardComponent key={key} />;
          })}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {g && !g.completed && (
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1 }}>Why this order</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    A quote needs a place, a hotel with a contract and transport. After these steps your first quote is
                    priced from your own data, and you have seen where everything goes.
                  </Typography>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} component="h2">Ask the assistant</Typography>
                  <AutoAwesomeRounded color="primary" fontSize="small" />
                </Stack>
                <AssistantPanel />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {customising && layout.data && <CustomiseHomeDialog layout={layout.data} onClose={() => setCustomising(false)} />}
    </Box>
  );
}

// ---- Customise Home (FR-NAV-07/08) ----

interface CardRow { key: string; shown: boolean }

function CustomiseHomeDialog({ layout, onClose }: { layout: HomeLayout; onClose: () => void }) {
  const { hasAnyPermission } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  const allowed = useTileAllowed();

  // Every card the role offers, in the person's current order; hidden ones keep their place after the shown ones.
  const [rows, setRows] = useState<CardRow[]>(() => {
    const keys = [...layout.cards, ...layout.roleCards, ...HOME_CARDS.filter((c) => c.timeSensitive).map((c) => c.key)];
    return keys
      .filter((k, i) => keys.indexOf(k) === i)
      .filter((k) => {
        const meta = homeCardMeta(k);
        return !!meta && (!meta.perms?.length || hasAnyPermission(meta.perms));
      })
      .map((k) => ({ key: k, shown: !!homeCardMeta(k)?.timeSensitive || !layout.hiddenCards.includes(k) }));
  });
  const [shortcuts, setShortcuts] = useState<string[]>(() => layout.shortcuts.filter((k) => {
    const t = findHubTile(k);
    return !!t && allowed(t);
  }).slice(0, MAX_SHORTCUTS));

  const tileGroups = useMemo(() => {
    const groups = new Map<string, HubTileRef[]>();
    ALL_HUB_TILES.filter((r) => allowed(r.tile)).forEach((r) => {
      const label = `${r.hub} · ${r.group}`;
      groups.set(label, [...(groups.get(label) ?? []), r]);
    });
    return Array.from(groups.entries());
  }, [allowed]);

  const save = useMutation({
    mutationFn: (body: HomePreferences) => homeApi.savePreferences(body),
    onSuccess: (data) => {
      qc.setQueryData([HOME_LAYOUT_KEY], data);
      qc.invalidateQueries({ queryKey: [HOME_LAYOUT_KEY] });
    },
  });

  const move = (i: number, by: -1 | 1) => setRows((r) => {
    const next = [...r];
    const j = i + by;
    if (j < 0 || j >= next.length) return r;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const toggleShown = (key: string) => setRows((r) => r.map((x) => (x.key === key ? { ...x, shown: !x.shown } : x)));
  const toggleShortcut = (key: string) => setShortcuts((s) => (s.includes(key)
    ? s.filter((k) => k !== key)
    : s.length >= MAX_SHORTCUTS ? s : [...s, key]));

  const submit = async (body: HomePreferences, message: string) => {
    try {
      await save.mutateAsync(body);
      notify.success(message);
      onClose();
    } catch (e) {
      notify.error(e, 'Home could not be saved. Please try again.');
    }
  };

  const onSave = () => submit({
    cardOrder: rows.map((r) => r.key),
    hiddenCards: rows.filter((r) => !r.shown && !homeCardMeta(r.key)?.timeSensitive).map((r) => r.key),
    shortcuts,
  }, 'Home saved.');
  const onReset = () => submit({ cardOrder: [], hiddenCards: [], shortcuts: [] }, 'Home is back to your role default.');

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Customise Home</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Cards</Typography>
        <Stack spacing={0.75} sx={{ mb: 3 }}>
          {rows.map((r, i) => {
            const meta = homeCardMeta(r.key)!;
            return (
              <Stack key={r.key} direction="row" alignItems="center" spacing={1}
                sx={{ px: 1.5, py: 0.75, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{meta.label}</Typography>
                  {meta.timeSensitive && (
                    <Typography variant="caption" color="text.secondary">Always shown: time-sensitive</Typography>
                  )}
                </Box>
                <IconButton size="small" aria-label={`Move ${meta.label} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                  <ArrowUpwardRounded fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label={`Move ${meta.label} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
                  <ArrowDownwardRounded fontSize="small" />
                </IconButton>
                <Switch
                  size="small"
                  checked={r.shown}
                  disabled={!!meta.timeSensitive}
                  onChange={() => toggleShown(r.key)}
                  inputProps={{ 'aria-label': `Show ${meta.label}` }}
                />
              </Stack>
            );
          })}
        </Stack>

        <Stack direction="row" alignItems="baseline" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>Shortcuts</Typography>
          <Typography variant="caption" color={shortcuts.length >= MAX_SHORTCUTS ? 'warning.main' : 'text.secondary'} fontWeight={600}>
            {shortcuts.length} of {MAX_SHORTCUTS}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Pick up to {MAX_SHORTCUTS} screens to open straight from Home.
        </Typography>
        <Stack spacing={1.5}>
          {tileGroups.map(([label, refs]) => (
            <Box key={label}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>{label}</Typography>
              <FormGroup>
                {refs.map(({ tile }) => {
                  const checked = shortcuts.includes(tile.key);
                  return (
                    <FormControlLabel
                      key={tile.key}
                      control={(
                        <Checkbox size="small" checked={checked} onChange={() => toggleShortcut(tile.key)}
                          disabled={!checked && shortcuts.length >= MAX_SHORTCUTS} />
                      )}
                      label={<Typography variant="body2">{tile.label}</Typography>}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onReset} disabled={save.isPending} sx={{ mr: 'auto' }}>Reset</Button>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={save.isPending}>Save</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---- Masters needing attention and shortcuts ----

/** FR-NAV-05/07: master tiles with records waiting for a decision, e.g. "3 pending · Transport rate cards". */
function MastersAttentionCard() {
  const allowed = useTileAllowed();
  const summary = useQuery({ queryKey: [MASTERS_SUMMARY_KEY], queryFn: mastersApi.summary, staleTime: 60_000 });
  const rows = (summary.data ?? [])
    .filter((c) => c.attention > 0)
    .map((c) => ({ count: c, tile: findHubTile(c.key) }))
    .filter((r): r is { count: MasterTileCount; tile: HubTile } => !!r.tile && allowed(r.tile));
  if (rows.length === 0) return null;
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1 }}>Masters needing attention</Typography>
        <Stack spacing={0.75}>
          {rows.map(({ count, tile }) => (
            <Link key={tile.key} component={RouterLink} to={tile.to} underline="hover" variant="body2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
              {tile.icon}
              <span>
                <b>{count.attention.toLocaleString('en-IN')} {count.attentionText ?? 'need attention'}</b> · {tile.label}
              </span>
            </Link>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** FR-NAV-07: the person's own shortcuts, picked in Customise Home. */
function ShortcutsCard() {
  const layout = useHomeLayout();
  const tiles = useAllowedTiles(layout.data?.shortcuts);
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1.25 }}>My shortcuts</Typography>
        {tiles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Pin up to {MAX_SHORTCUTS} shortcuts with Customise.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tiles.map((t) => (
              <Button key={t.key} component={RouterLink} to={t.to} variant="outlined" size="small" startIcon={t.icon}
                sx={{ textTransform: 'none' }}>
                {t.label}
              </Button>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Guided setup as a Home card ----

function GuidedSetupHomeCard() {
  const guide = useSetupGuide();
  const g = guide.data;
  return (
    <Box sx={{ mb: 2 }}>
      <DataState compact loading={guide.isLoading} loadingText="Loading your setup…" error={guide.error} onRetry={guide.refetch}>
        {g && (g.completed ? (
          <Alert severity="success" icon={<CheckCircleRounded />}>
            Guided setup is complete — every step has a real entry.
          </Alert>
        ) : (
          <GuidedSetupCard guide={g} />
        ))}
      </DataState>
    </Box>
  );
}

// ---- Trial ----

function daysText(n: number) {
  return `${n} ${n === 1 ? 'day' : 'days'} left`;
}

function trialSummary(g: SetupGuide): string | null {
  if (g.trial.status === 'Trial') return `trial: ${daysText(g.trial.daysLeft)}`;
  if (g.trial.status === 'Ended') return 'trial ended';
  return null;
}

function TrialBanner({ guide }: { guide: SetupGuide }) {
  const { trial } = guide;
  if (trial.status !== 'Trial' && trial.status !== 'Ended') return null;
  const ended = trial.status === 'Ended';
  const kyc = guide.steps.find((s) => s.key === 'kyc');
  // Once sent, there is nothing to start: it is waiting for TravelERP. Sent back, it needs another look.
  const kycSent = trial.kycStatus === 'Submitted';
  const kycBack = trial.kycStatus === 'Rejected';
  const kycLink = trial.kycApproved || !kyc?.available ? null : kycSent ? (
    <> <strong>KYC submitted · waiting for approval.</strong></>
  ) : (
    <>
      {' '}
      <Link component={RouterLink} to={kyc.path} fontWeight={600} underline="hover">{kycBack ? 'KYC sent back · review it' : 'Start KYC'}</Link>
    </>
  );

  return (
    <Alert
      severity={ended ? 'error' : 'warning'}
      icon={<WarningAmberRounded />}
      sx={{ mb: 2.5, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
    >
      <strong>{ended ? 'Trial ended.' : `Trial · ${daysText(trial.daysLeft)}.`}</strong>{' '}
      {ended
        ? `Your free trial ended on ${formatDate(trial.endsAt, 'D MMM YYYY')}. Contact us to choose a plan or ask for more time.`
        : 'Everything works for practice.'}
      {!trial.kycApproved && ' Real invoices, payment links and vendor invites unlock after KYC is approved.'}
      {kycLink}
    </Alert>
  );
}

// ---- Guided setup ----

function GuidedSetupCard({ guide }: { guide: SetupGuide }) {
  const pct = guide.total ? Math.round((guide.doneCount / guide.total) * 100) : 0;
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2">
          Guided setup · {guide.doneCount} of {guide.total} done
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label={`${pct}% of guided setup done`}
          sx={{ height: 8, borderRadius: 4, my: 1.75 }}
        />
        <Stack spacing={1.25}>
          {guide.steps.map((s, i) => (
            <StepRow
              key={s.key}
              step={s}
              primary={!s.available || s.status === 'Done' ? false : guide.steps.findIndex((x) => x.available && x.status === 'Todo') === i}
              channels={s.key === 'channels' ? guide.channels : undefined}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.75, lineHeight: 1.6 }}>
          Add at least one real entry in each step; each opens the real screen with tips. Skip any step and come
          back; this list stays on Home until done.
        </Typography>
      </CardContent>
    </Card>
  );
}

function StepRow({ step, primary, channels }: { step: SetupStep; primary: boolean; channels?: SetupGuide['channels'] }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const skip = useSkipSetupStep();
  const done = step.status === 'Done';
  const skipped = step.status === 'Skipped';

  const open = () => navigate(`${step.path}?guide=${encodeURIComponent(step.key)}`);
  const toggleSkip = async () => {
    try {
      await skip.mutateAsync({ key: step.key, skip: !skipped });
    } catch (e) {
      notify.error(e);
    }
  };

  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        border: 1,
        borderRadius: 2,
        borderColor: done ? alpha(t.palette.success.main, 0.35) : 'divider',
        bgcolor: done ? alpha(t.palette.success.main, 0.06) : 'background.paper',
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
        {done ? (
          <CheckCircleRounded color="success" sx={{ flexShrink: 0 }} />
        ) : (
          <RadioButtonUncheckedRounded sx={{ color: 'text.disabled', flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {step.title}
          </Typography>
          {channels && !done && step.available && (
            <Typography variant="caption" color="text.secondary">
              Email test {channels.emailTested ? '✓ sent' : 'not sent yet'} · WhatsApp test{' '}
              {channels.whatsAppTested ? '✓ sent' : 'not sent yet'}
            </Typography>
          )}
        </Box>
        {skipped && <Chip size="small" label="Skipped" variant="outlined" />}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: { sm: 0.5 } }}>
          {step.minutes} min
        </Typography>
        {done ? null : step.available ? (
          <>
            <Can perm="Company.Edit">
              <Button size="small" color="inherit" onClick={toggleSkip} disabled={skip.isPending}>
                {skipped ? 'Resume' : 'Skip'}
              </Button>
            </Can>
            <Button size="small" variant={primary ? 'contained' : 'outlined'} onClick={open} sx={{ ml: { xs: 'auto', sm: 0 } }}>
              Start
            </Button>
          </>
        ) : (
          <Tooltip title="Opens when this module is switched on for your company.">
            {/* The span keeps the tooltip working on a disabled button. */}
            <span style={{ marginLeft: 'auto' }}>
              <Button size="small" variant="outlined" disabled>Start</Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

/**
 * FR-NAV-07/08: approvals waiting always reach the admin on Home, without opening any menu (vendor KYC, rate changes,
 * hotels and contracts, transport rate cards).
 */
function ApprovalsCard() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const canVendors = hasAnyPermission(['Vendor.Approve']);
  const canHotels = hasAnyPermission(['Hotel.Approve']);
  const canTransport = hasAnyPermission(['Transport.Approve']);
  const canCustomers = hasAnyPermission(['Customer.Approve']);
  const customers = useQuery({ queryKey: [CUSTOMER_APPROVALS_KEY], queryFn: customersApi.approvals, enabled: canCustomers, staleTime: 60_000 });
  const list = useQuery({ queryKey: [APPROVALS_KEY, 'list'], queryFn: approvalsApi.list, enabled: canVendors, staleTime: 60_000 });
  const hotels = useQuery({ queryKey: [HOTEL_APPROVALS_KEY, 'list'], queryFn: hotelReviewApi.approvals, enabled: canHotels, staleTime: 60_000 });
  const transport = useQuery({ queryKey: [TRANSPORT_APPROVALS_KEY, 'list'], queryFn: transportReviewApi.approvals, enabled: canTransport, staleTime: 60_000 });
  const items = [
    ...(canVendors ? list.data?.items ?? [] : []),
    ...(canHotels ? hotels.data ?? [] : []).map((h) => ({ kind: h.kind, id: h.id, title: h.title, subtitle: h.subtitle })),
    ...(canTransport ? transport.data ?? [] : []).map((t) => ({ kind: 'TransportCard', id: t.id, title: t.title, subtitle: t.subtitle })),
    ...(canCustomers ? customers.data ?? [] : []).map((c) => ({ kind: `Customer${c.kind}`, id: c.id, title: c.title, subtitle: c.subtitle })),
  ];
  if (items.length === 0) return null;
  return (
    <Card sx={{ mb: 2, borderColor: 'warning.light' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <WarningAmberRounded color="warning" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ flex: 1 }}>Approvals waiting ({items.length})</Typography>
          <Button variant="contained" size="small" onClick={() => navigate('/approvals')}>Open Approvals centre</Button>
        </Stack>
        <Stack spacing={0.5}>
          {items.slice(0, 5).map((i) => (
            <Typography key={`${i.kind}-${i.id}`} variant="body2"><b>{i.title}</b> · {i.subtitle}</Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * M5.4 L8: my follow-ups today — time, lead, customer, how, Due or Overdue (red) — with the overdue count; a manager also sees
 * the team's overdue follow-ups.
 */
function FollowUpsTodayCard() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const can = hasAnyPermission(LEAD_VIEW);
  const q = useQuery({ queryKey: [LEAD_HOME_KEY], queryFn: leadsApi.home, enabled: can, refetchInterval: 120_000, staleTime: 60_000 });
  const h = q.data;
  if (!can || !h) return null;
  return (
    <Card sx={{ mb: 2, borderColor: h.overdue ? 'error.light' : undefined }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }} spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ flex: 1 }}>My follow-ups today ({h.today.length})</Typography>
          {h.overdue > 0 && <Chip size="small" color="error" icon={<WarningAmberRounded />} label={`${h.overdue} overdue`} />}
          {h.teamOverdue > 0 && <Chip size="small" variant="outlined" color="error" label={`Team: ${h.teamOverdue} overdue`} />}
          <Button size="small" onClick={() => navigate('/leads')}>Open leads</Button>
        </Stack>
        {h.today.length === 0 ? <Typography variant="body2" color="text.secondary">No follow-ups today.</Typography> : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 460, '& td, & th': { py: 0.75, px: 1, borderBottom: '1px solid', borderColor: 'divider', fontSize: 14, textAlign: 'left' } }}>
              <thead><tr><Box component="th">Time</Box><Box component="th">Lead</Box><Box component="th">Customer</Box><Box component="th">How</Box><Box component="th" /></tr></thead>
              <tbody>
                {h.today.map((f) => (
                  <Box component="tr" key={`${f.leadId}-${f.dueAt}`} onClick={() => navigate(`/leads/${f.leadId}`)}
                    sx={{ cursor: 'pointer', bgcolor: f.overdue ? '#FCEFEF' : undefined, '&:hover': { bgcolor: f.overdue ? '#F9E1E1' : 'action.hover' } }}>
                    <td>{new Date(f.dueAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</td>
                    <td><b>{f.leadCode}</b></td><td>{f.customer}</td><td>{f.mode}</td>
                    <td>{f.overdue
                      ? <Typography component="span" fontSize={13} fontWeight={700} color="error.main">{f.dueText || 'Overdue'}</Typography>
                      : <Chip size="small" color="primary" variant="outlined" label="Due" sx={{ height: 20 }} />}</td>
                  </Box>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The same reminder also goes to the mobile app, WhatsApp and email ({h.reminderText}).
        </Typography>
        {h.overdue > 0 && (
          <Alert severity="error" icon={false} sx={{ mt: 1.5, py: 0.5 }}>
            <b>Overdue: {h.overdue}</b> · follow-up not recorded after its time. Your manager is alerted after{' '}
            {h.managerAlertAfterMinutes % 60 === 0 ? `${h.managerAlertAfterMinutes / 60} hour${h.managerAlertAfterMinutes === 60 ? '' : 's'}` : `${h.managerAlertAfterMinutes} minutes`} (set in Settings).
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/** M5.4 L8: my open leads by stage; a click opens the list. */
function MyLeadsCard() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const can = hasAnyPermission(LEAD_VIEW);
  const q = useQuery({ queryKey: [LEAD_HOME_KEY], queryFn: leadsApi.home, enabled: can, refetchInterval: 120_000, staleTime: 60_000 });
  if (!can || !q.data) return null;
  const by = q.data.myLeads;
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ mb: 1 }}>My leads</Typography>
        <Grid container spacing={1}>
          {OPEN_STAGES.map((s) => (
            <Grid key={s} size={{ xs: 6, sm: 3 }}>
              <Box onClick={() => navigate('/leads')} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}>
                <Typography fontSize={24} fontWeight={700}>{by[s] ?? 0}</Typography>
                <Typography variant="body2" color="text.secondary">{STAGE_LABEL[s]}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

/** FR-TRN-15: mappings whose rate card ends within 30 days, in red on Home, so next season's card is loaded in time. */
function TransportExpiryCard() {
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const can = hasAnyPermission(['Transport.View']);
  const q = useQuery({ queryKey: [MAPPINGS_KEY, 'home'], queryFn: () => mappingsApi.list({}), enabled: can, staleTime: 5 * 60_000 });
  const rows = (q.data?.rows ?? []).filter((r) => r.expiresSoon);
  if (!can || rows.length === 0) return null;
  return (
    <Card sx={{ mb: 2, borderColor: 'error.light', bgcolor: '#FCEFEF' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <WarningAmberRounded color="error" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} component="h2" sx={{ flex: 1, color: 'error.dark' }}>
            Transport rate cards ending soon ({rows.length} mapping{rows.length === 1 ? '' : 's'})
          </Typography>
          <Button variant="contained" color="error" size="small" onClick={() => navigate('/transport-mapping')}>Open Transport mapping</Button>
        </Stack>
        <Stack spacing={0.5}>
          {rows.slice(0, 6).map((r) => (
            <Typography key={r.id} variant="body2"><b>{r.regionName}</b> from {r.startLabel} · {r.cardText} · ends {r.cardValidTill ? formatDate(r.cardValidTill) : ''}</Typography>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The transporters are emailed for next season's rate sheet. Load and approve the new card before these end, or quotes will use the backup card.
        </Typography>
      </CardContent>
    </Card>
  );
}
